import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import {
  GRID_DATA_SOURCE,
  type GridBackendAttributeFilter,
  type GridBucketRowsPage,
  type GridBucketScope,
  type GridDataSource,
  type GridGroupSummaryItem,
  type GridGroupSummaryPage,
  type GridGroupTreePage,
  type GridRowsPage,
  type GridSortField
} from '../contracts/grid-data-source.interface';
import type { GridQueryFilter } from '../contracts/grid-query-filter.type';

/**
 * Internal orchestration-facing wrapper over {@link GridDataSource}.
 * Preserves the legacy method shapes used by extracted orchestration modules
 * while routing all network access through the host-provided port.
 */
@Injectable()
export class GridOrchestrationDataAccess {
  private readonly dataSource: GridDataSource = inject(GRID_DATA_SOURCE);

  /**
   * Fetches a page of grid rows through the host {@link GridDataSource}.
   *
   * @param options - Pagination, filter, and sort arguments forwarded to `fetchRows`.
   * @returns The paginated row page from the host data source.
   */
  public getElementsForGrid(options?: {
    first?: number;
    after?: string;
    where?: GridQueryFilter;
    sort?: ReadonlyArray<GridSortField>;
    attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>;
  }): Observable<GridRowsPage> {
    return this.dataSource.fetchRows(options ?? {});
  }

  public getGroupSummaryTree(
    fields: ReadonlyArray<string>,
    scopes: ReadonlyArray<string>,
    options?: {
      where?: GridQueryFilter;
      attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>;
      first?: number;
      after?: string;
    }
  ): Observable<GridGroupTreePage> {
    return this.dataSource.fetchGroupTree({
      fields,
      scopes,
      ...options
    });
  }

  public getGroupSummaryPaginated(
    field: string,
    scopes: ReadonlyArray<string>,
    options?: {
      where?: GridQueryFilter;
      attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>;
      first?: number;
      after?: string;
    }
  ): Observable<GridGroupSummaryPage> {
    return this.dataSource.fetchGroupSummary({
      field,
      scopes,
      ...options
    });
  }

  public getGroupSummary(
    field: string,
    scopes: ReadonlyArray<string>,
    where?: GridQueryFilter,
    attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>
  ): Observable<Array<GridGroupSummaryItem>> {
    return this.dataSource
      .fetchGroupSummaryList({
        field,
        scopes,
        where,
        attributeFilter
      })
      .pipe(map((items) => [...items]));
  }

  public getElementsForGroupBuckets(options: {
    buckets: ReadonlyArray<GridBucketScope>;
    sharedWhere: GridQueryFilter;
    attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>;
    sort?: ReadonlyArray<GridSortField>;
    firstPerBucket?: number;
  }): Observable<Array<GridBucketRowsPage>> {
    return this.dataSource.fetchBucketRows(options).pipe(map((items) => [...items]));
  }
}
